import { c as _c } from "react/compiler-runtime";
import { useMutation } from '@apollo/client/react';
import { Box, Button, Link, Spinner } from '@chakra-ui/react';
import { useToast } from '@src/components/chakra-ui/use-toast';
import { stripMarkdownWhitespace } from '@src/components/sco/Chat/ChatMessageComposer';
import { CommentPendingAttachments, COMMENT_STORED_FILES_SELECTION, useCommentAttachments } from '@src/components/sco/Comments/CommentAttachments';
import { editorTypographyStyles, RichMarkdownInput } from '@src/components/sco/RichMarkdownInput';
import { ScoComment } from '@src/graphql-types';
import { gql } from '@src/lib/gql';
import React, { useState } from 'react';
type CommentEditorProps = {
  comment: ScoComment;
  /** The `MarkdownRenderer` preset the comment is shown with, so the editor looks the same */
  markdownPreset?: string;
  onSuccess: () => void;
  onCancel: () => void;
  onError: () => void;
};
export const CommentEditor = props => {
  const $ = _c(41);
  const {
    comment
  } = props;
  const [body, setBody] = useState(comment.body);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = CommentEditor.mutations.updateComment();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [updateComment] = useMutation(t0);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();
  const t1 = comment.storedFiles?.items;
  let t2;
  if ($[1] !== t1) {
    t2 = {
      initialFiles: t1
    };
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    pendingFiles,
    addFile,
    removeFile,
    isUploading,
    isFull,
    storedFileIds
  } = useCommentAttachments(t2);
  let t3;
  if ($[3] !== body || $[4] !== isUploading || $[5] !== props || $[6] !== storedFileIds || $[7] !== toast || $[8] !== updateComment) {
    t3 = async () => {
      const hasBody = stripMarkdownWhitespace(body || "").length > 0;
      if (!hasBody && storedFileIds.length === 0 || isUploading) {
        return;
      }
      await setIsLoading(true);
      ;
      try {
        await updateComment({
          variables: {
            input: {
              commentId: props.comment.commentId,
              body,
              storedFileIds
            }
          }
        });
        await props.onSuccess();
        toast({
          title: "Comment has been updated.",
          isClosable: true,
          status: "info",
          containerStyle: {
            color: "black"
          }
        });
      } catch (t4) {
        const err = t4;
        console.log(err);
        props.onError();
      }
      await setIsLoading(false);
    };
    $[3] = body;
    $[4] = isUploading;
    $[5] = props;
    $[6] = storedFileIds;
    $[7] = toast;
    $[8] = updateComment;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const onSave = t3;
  const t4 = isFull || isUploading || isLoading;
  const t5 = comment.body;
  const t6 = props.markdownPreset || "ugc";
  let t7;
  if ($[10] !== t6) {
    t7 = editorTypographyStyles(t6);
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== props) {
    t8 = <Link key="cancel-link" color="purpleLight !important" fontFamily="Poppins" fontSize="xs" fontWeight="semibold" justifySelf="flex-end" letterSpacing="-0.25px" textDecoration="none !important" onClick={() => props.onCancel && props.onCancel()}>Cancel</Link>;
    $[12] = props;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== onSave) {
    t9 = () => onSave();
    $[14] = onSave;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== isLoading || $[17] !== isUploading) {
    t10 = isLoading || isUploading ? <Spinner /> : "Save";
    $[16] = isLoading;
    $[17] = isUploading;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== isUploading || $[20] !== t10 || $[21] !== t9) {
    t11 = <Button key="save-button" disabled={isUploading} justifySelf="flex-end" marginLeft="auto" marginRight="4px" size="xs" variant="cta" onClick={t9}>{t10}</Button>;
    $[19] = isUploading;
    $[20] = t10;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11 || $[24] !== t8) {
    t12 = [t8, t11];
    $[23] = t11;
    $[24] = t8;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  let t14;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      borderColor: "purpleLightAlpha.500",
      borderRadius: "lg"
    };
    t14 = values => setBody(values.markdown);
    $[26] = t13;
    $[27] = t14;
  } else {
    t13 = $[26];
    t14 = $[27];
  }
  let t15;
  if ($[28] !== addFile || $[29] !== comment.body || $[30] !== t12 || $[31] !== t4 || $[32] !== t7) {
    t15 = <RichMarkdownInput enableFileAttachments={true} fileAttachmentsDisabled={t4} initialMarkdownValue={t5} placeholder="Enter the details..." typographyStyles={t7} additionalToolbarButtons={t12} boxProps={t13} onChange={t14} onFileAttachmentSelected={addFile} />;
    $[28] = addFile;
    $[29] = comment.body;
    $[30] = t12;
    $[31] = t4;
    $[32] = t7;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== isLoading || $[35] !== pendingFiles || $[36] !== removeFile) {
    t16 = <CommentPendingAttachments disabled={isLoading} files={pendingFiles} onRemove={removeFile} />;
    $[34] = isLoading;
    $[35] = pendingFiles;
    $[36] = removeFile;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t15 || $[39] !== t16) {
    t17 = <Box>{t15}{t16}</Box>;
    $[38] = t15;
    $[39] = t16;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  return t17;
};
CommentEditor.mutations = {
  updateComment: () => gql`
    mutation($input: UpdateCommentInput!) {
      updateComment(input: $input) {
        comment {
          id
          ${CommentEditor.fragments.ScoComment()}
        }
      }
    }
  `
};
CommentEditor.fragments = {
  ScoComment: () => gql`
    fragment _ on ScoComment {
      id
      body
      updatedAt
      ${COMMENT_STORED_FILES_SELECTION}
    }
  `
};